/**
 * Paying from a link.
 *
 * The second screen in the app a stranger can reach, and like the signing one
 * it never touches the database: everything goes through the worker, carrying
 * the link token, and the worker reads the amount out of the charges row. No
 * figure on this page can change what is charged.
 *
 * Unlike signing there is no code to type. A signing link leaking puts
 * somebody else's mark on a title application, which is worth a code read out
 * over the telephone; a payment link leaking lets a stranger pay us money,
 * which is not worth making every customer phone in for. The reasoning is
 * written out in 32_charges.sql.
 *
 * The receipt is drawn here, in the customer's own browser, from the figures
 * the worker returns — so somebody paying at eleven at night has their receipt
 * without waiting for anyone to print one. The same code draws the copy the
 * desk prints and files.
 */

import { useEffect, useState } from "react";
import { Button, ErrorNote, LogoMark } from "../../components/ui";
import { formatMoney } from "../../lib/money";
import { openCharge, type OpenCharge, type PaidCharge } from "../../lib/stripe";
import { buildChargeReceipt } from "../../forms/chargeReceipt";
import ChargeCard from "../payments/ChargeCard";

/** Stored as ten digits; nobody reads a phone number that way. */
const prettyPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw;
};

export default function RemotePay({ token }: { token: string }) {
  const [charge, setCharge] = useState<OpenCharge | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [downloading, setDownloading] = useState(false);

  /* The brand, the last four and the time it was paid are written by the
     worker, and a receipt without them is not much of a receipt. They arrive
     with the record call rather than from a second read, because paying
     retires the token: the link this page was opened with no longer answers. */
  const settle = (row: PaidCharge | null) => {
    setPaid(true);
    if (row) {
      setCharge((c) => (c ? {
        ...c,
        status: "paid",
        paidAt: row.paid_at,
        cardBrand: row.card_brand,
        cardLast4: row.card_last4,
      } : c));
    }
  };

  useEffect(() => {
    let live = true;
    openCharge(token)
      .then((c) => { if (live) setCharge(c); })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [token]);

  /* Already paid when the page was opened — the customer came back to the
     same link, or paid on another device. Shown as a receipt, not as a card
     field, which is also what stops a second payment going through. */
  const settled = paid || charge?.status === "paid" || charge?.status === "refunded";

  async function downloadReceipt() {
    if (!charge) return;
    setDownloading(true);
    try {
      const bytes = await buildChargeReceipt({
        chargeNo: charge.chargeNo,
        amountCents: charge.amountCents,
        description: charge.description,
        customerName: charge.customerName,
        vehicle: charge.vehicle,
        vin: charge.vin,
        cardBrand: charge.cardBrand,
        cardLast4: charge.cardLast4,
        /* Freshly paid: the row has the timestamp but this page has not
           re-read it, and a receipt with no date on it is not a receipt. */
        paidAt: charge.paidAt ?? new Date().toISOString(),
        dealer: charge.dealer,
      });
      const buffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buffer).set(bytes);
      const url = URL.createObjectURL(new Blob([buffer], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${charge.chargeNo}-receipt.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not build the receipt.");
    } finally {
      setDownloading(false);
    }
  }

  const dealerName = charge?.dealer
    ? (charge.dealer.dba || charge.dealer.legalName)
    : "Cape Auto Solutions";

  return (
    <div className="signshell">
      <header className="signhead">
        <LogoMark size={30} />
        <span>{dealerName}</span>
      </header>

      <main className="signmain">
        {err && <ErrorNote>{err}</ErrorNote>}

        {!charge && !err && <p className="lede">Loading</p>}

        {charge && !settled && (
          <>
            <h1>Pay {formatMoney(charge.amountCents)}</h1>
            <p className="lede">
              {charge.customerName}
              {charge.description ? ` — ${charge.description}` : ""}
            </p>

            <dl className="spec">
              <div><dt>Amount</dt><dd>{formatMoney(charge.amountCents)}</dd></div>
              {charge.description && (
                <div><dt>For</dt><dd>{charge.description}</dd></div>)}
              {charge.vehicle && (
                <div><dt>Vehicle</dt><dd>{charge.vehicle}</dd></div>)}
              <div><dt>Reference</dt><dd className="mono">{charge.chargeNo}</dd></div>
            </dl>

            <ChargeCard
              ref_={{ token }}
              amountCents={charge.amountCents}
              payLabel="Pay"
              onPaid={settle} />

            <p className="finehint">
              Questions about this payment? Call {dealerName}
              {charge.dealer?.phone ? ` on ${prettyPhone(charge.dealer.phone)}` : ""}.
            </p>
          </>
        )}

        {charge && settled && (
          <div className="signdone">
            <h1>{charge.status === "refunded" ? "Refunded" : "Paid"}</h1>
            <p className="lede">
              {charge.status === "refunded"
                ? `This payment of ${formatMoney(charge.amountCents)} has been refunded to your card.`
                : `Thank you${charge.customerName ? `, ${charge.customerName.split(" ")[0]}` : ""}. `
                  + `${formatMoney(charge.amountCents)} has been paid.`}
            </p>
            {charge.status !== "refunded" && (
              <>
                <p className="lede">
                  A confirmation is on its way to your email. Your reference is{" "}
                  <span className="mono">{charge.chargeNo}</span>.
                </p>
                <Button onClick={() => void downloadReceipt()} disabled={downloading}>
                  {downloading ? "Building" : "Download the receipt"}
                </Button>
              </>
            )}
            <p className="finehint">You can close this page.</p>
          </div>
        )}
      </main>
    </div>
  );
}
